import React from 'react';
import { useTheme } from '@chakra-ui/react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useAxisStyles, useChartColors } from 'src/hooks/colors/useChartColors';
import { TooltipContent } from '../../atoms/tooltips/TooltipContent';
import { useLegendContext } from '../Legend/LegendContext';

export type BarGraphProps = {
  data: object[];
  xAxisKey: string;
  stacked: boolean;
  xAxisFormatter?: (value: number | string) => string;
  xAxisTooltipFormatter?: (value: number | string) => string;
  yAxisFormatter?: (value: number) => string;
  percentRange?: boolean;
};

// Requires use of legend
export const NewBarGraph: React.FC<BarGraphProps> = ({
  data,
  xAxisKey,
  stacked,
  xAxisTooltipFormatter,
  xAxisFormatter = (value: number | string) => value.toString(),
  yAxisFormatter = (value: number) => value.toString(),
  percentRange,
}) => {
  const axisStyles = useAxisStyles();
  const theme = useTheme();

  const { selectedBaseLegendValues } = useLegendContext();

  const tooltipLabelFormatter = xAxisTooltipFormatter ?? xAxisFormatter;

  const { gridColor } = useChartColors();

  return (
    <ResponsiveContainer>
      <ComposedChart layout={'horizontal'} data={data}>
        <CartesianGrid
          strokeDasharray="4 4"
          strokeWidth={1}
          stroke={gridColor}
          strokeOpacity={0.4}
          fill="transparent"
          vertical={false}
        />
        <YAxis
          width={70}
          domain={percentRange ? [0, 100] : undefined}
          tickFormatter={yAxisFormatter}
          {...axisStyles}
          axisLine={false}
        />
        <XAxis
          dataKey={xAxisKey}
          padding={{ left: 16, right: 16 }}
          tickFormatter={xAxisFormatter}
          {...axisStyles}
          axisLine={false}
        />
        <Tooltip
          offset={25}
          cursor={{
            stroke: theme.colors.chart.line.blue,
            strokeOpacity: 0.6,
            strokeWidth: 2,
            strokeDasharray: '5 5',
          }}
          content={
            <TooltipContent
              valueFormatter={yAxisFormatter}
              labelFormatter={tooltipLabelFormatter}
            />
          }
        />
        {selectedBaseLegendValues.map((v, index) => {
          const isTopBar = index === selectedBaseLegendValues.length - 1;
          const shouldHaveRadius = stacked ? isTopBar : true;

          return (
            <Bar
              key={v.key}
              dataKey={v.key}
              stackId={stacked ? 'a' : v.key}
              fill={v.color}
              isAnimationActive={false}
              radius={shouldHaveRadius ? [4, 4, 0, 0] : undefined}
            />
          );
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
